import React, { useState } from "react";
import { Sparkles, Loader2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useStudio } from "@/studio/StudioContext";
import { useAuth } from "@/auth/AuthContext";
import { useSubscription } from "@/subscription/SubscriptionContext";
import { formatApiErrorDetail } from "@/auth/AuthContext";

const VIBES = [
  "Dark trap, 140 BPM",
  "Chill lo-fi hip hop",
  "Classic boom-bap",
  "Deep house groove",
  "Uplifting pop beat",
  "Aggressive drill",
  "Smooth R&B",
  "Energetic techno",
];

export default function AIStarter() {
  const { generateSongStarter, setActiveTab, play } = useStudio();
  const { requireAuth } = useAuth();
  const { openUpgrade } = useSubscription();
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);

  const openModal = () => {
    if (!requireAuth()) return;
    setOpen(true);
  };

  const generate = async (text) => {
    const p = (text || prompt).trim();
    if (!p) {
      toast.error("Describe a vibe first");
      return;
    }
    setBusy(true);
    try {
      const data = await generateSongStarter(p);
      setOpen(false);
      setActiveTab("beats");
      play();
      toast.success(`Built a ${data.genre || "custom"} starter — now playing`);
    } catch (e) {
      if (e.response?.status === 402) {
        setOpen(false);
        openUpgrade(formatApiErrorDetail(e.response?.data?.detail));
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not generate a beat");
      }
    }
    setBusy(false);
  };

  return (
    <>
      <button
        data-testid="ai-start-btn"
        onClick={openModal}
        className="flex items-center gap-1.5 px-3 py-2 rounded-sm text-xs font-bold text-black transition-all hover:scale-[1.03]"
        style={{
          background: "linear-gradient(120deg,#a855f7,#00e5ff)",
          boxShadow: "0 0 14px rgba(168,85,247,0.45)",
        }}
        title="Let AI build a starting beat for you"
      >
        <Sparkles size={14} /> AI Start
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0f0f0f] border-[#262626] text-white max-w-md" data-testid="ai-starter-modal">
          <DialogHeader>
            <DialogTitle className="font-display tracking-widest flex items-center gap-2">
              <Wand2 size={18} className="text-[#a855f7]" />
              BEAT STARTER
            </DialogTitle>
            <DialogDescription className="text-neutral-500 text-xs">
              Describe a vibe and BeatMXR lays down a <b className="text-neutral-300">drum beat</b> in the drum machine for you to build on — add your own bass, keys and melodies.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-3 mt-1">
            <input
              data-testid="ai-prompt-input"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !busy && generate()}
              placeholder="e.g. dark trap, 140 BPM, moody"
              disabled={busy}
              className="bg-[#050505] border border-[#262626] rounded-sm px-3 py-2.5 text-sm outline-none focus:border-[#a855f7] transition-colors"
            />

            <div className="flex flex-wrap gap-1.5">
              {VIBES.map((v) => (
                <button
                  key={v}
                  data-testid={`ai-vibe-${v.split(",")[0].toLowerCase().replace(/\s+/g, "-")}`}
                  disabled={busy}
                  onClick={() => {
                    setPrompt(v);
                    generate(v);
                  }}
                  className="text-[11px] px-2.5 py-1 rounded-full border border-[#333] text-neutral-300 hover:border-[#a855f7] hover:text-white transition-colors disabled:opacity-40"
                >
                  {v}
                </button>
              ))}
            </div>

            <button
              data-testid="ai-generate-btn"
              onClick={() => generate()}
              disabled={busy}
              className="flex items-center justify-center gap-2 rounded-sm py-2.5 text-sm font-bold text-black transition-colors disabled:opacity-50"
              style={{ background: "linear-gradient(120deg,#a855f7,#00e5ff)" }}
            >
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
              {busy ? "Building your beat…" : "Generate Beat"}
            </button>
            <span className="text-[10px] text-neutral-600 text-center">
              Drums only — add your own instruments after. Want a full studio-quality beat with instruments? Use <b className="text-[#ffb800]">Master</b> in the Beat Lab (Pro).
            </span>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
